import { createInfiniteQuery } from 'react-query-kit';

import type { SchoolSearchRes } from '@/api/gen/_model/schoolSearchRes';
import { searchSchools } from '@/api/gen/school-controller/school-controller.gen';
import { retryOnServiceUnavailable } from '@/shared/utils/queryRetryBudget';

const PAGE_SIZE = 20;

const useSchoolSearchQuery = createInfiniteQuery<
  Awaited<ReturnType<typeof searchSchools>>,
  string,
  Error,
  number
>({
  queryKey: ['schoolSearch'],
  use: [retryOnServiceUnavailable],
  fetcher: (searchQuery, { pageParam, signal }) => {
    const req = { searchText: searchQuery, page: pageParam, limit: PAGE_SIZE };
    // 생성 타입은 `req` 로 감싸지만 서버는 평평한 쿼리 파라미터를 받아 options.params 로 덮어 보낸다.
    return searchSchools({ req }, { params: req }, signal);
  },
  initialPageParam: 1,
  getNextPageParam: (lastPage, _, lastPageParam) => {
    // 반환된 학교 수가 limit보다 작으면 마지막 페이지
    const hasMore = lastPage.data.content.length >= PAGE_SIZE;

    return hasMore ? lastPageParam + 1 : undefined;
  },
  refetchOnWindowFocus: false,
});

export const useSchoolSearch = (searchQuery: string, enabled: boolean) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useSchoolSearchQuery({
    variables: searchQuery,
    enabled: enabled && searchQuery.length > 0,
  });

  const schools: SchoolSearchRes[] =
    data?.pages.flatMap(page => page.data.content) ?? [];

  return {
    schools,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  };
};
